import { IconClock } from '@/assets/icons';
import { ImagesMobile } from '@/assets/images';
import { Button } from '@/components';
import { ROUTERS, ROUTERS_HIDDEN_KEY, TextMessage } from '@/constant';
import { useOnClickOutside } from '@/hooks';
import { MenuContext, useSessionTimerContext } from '@/routers/main-layout';
import {
	AuthActions,
	CommonActions,
	CommonSelectors,
	WorkerDocumentActions,
} from '@/stores';
import { MyPageSelectors } from '@/stores/my-page';
import { NOOP, showAlert } from '@/utils';
import { memo, useCallback, useContext, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import './style.scss';
import { includes } from 'ramda';

import { getMenuMobileItems, TItemMenuMobile } from './config';

const CLICK_OUTSIDE_IGNORE_SELECTORS = ['.container-alert'];

const MenuMobile = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const menuContext = useContext(MenuContext);
	const { email, isWorkerPublicAdminRole, role } = useSelector(
		MyPageSelectors.selectDataMyPage
	);
	const hasFormChanged = useSelector(CommonSelectors.selectFormHasChanged);
	const ref = useRef<HTMLDivElement>(null);
	const { pathname } = useLocation();
	const menuItems = useMemo(() => {
		let _menuItems = getMenuMobileItems() as TItemMenuMobile[];

		// Check role to hidden menu item
		if (isWorkerPublicAdminRole) {
			_menuItems = _menuItems?.map((item) => {
				let newItem = { ...item };
				const menuHiddenKeys = ROUTERS_HIDDEN_KEY[role];
				if (includes(item.key, menuHiddenKeys)) {
					return null as unknown as TItemMenuMobile;
				}

				return newItem;
			});
		}

		return _menuItems;
	}, [pathname, role]);

	useOnClickOutside(
		ref,
		() => {
			if (menuContext.menuOpen) {
				menuContext.onToggle();
			}
		},
		{ ignoreSelectors: CLICK_OUTSIDE_IGNORE_SELECTORS }
	);

	const onClick = useCallback(
		(item: TItemMenuMobile) => {
			if (item.key === pathname) {
				menuContext.onToggle();
				return;
			}
			if (hasFormChanged) {
				showAlert(
					TextMessage.CONFIRM_21,
					() => {
						navigate(item.key);
						dispatch(CommonActions.setHasFormChange(false));
					},
					NOOP
				);

				return;
			}

			navigate(item.key);
			dispatch(WorkerDocumentActions.handleNextStep(null));
			menuContext.onToggle();
		},
		[pathname, menuContext, hasFormChanged]
	);

	const handleClickProfile = useCallback(() => {
		menuContext.onToggle();
		navigate(ROUTERS.MP1_01);
		dispatch(WorkerDocumentActions.handleNextStep(null));
	}, [menuContext, navigate]);

	const onLogoutClick = useCallback(() => {
		dispatch(AuthActions.logoutRequest({ isCallApi: true }));
	}, []);

	const { extendCount, timer, extendLoading, onExtend } =
		useSessionTimerContext();

	return (
		<div
			className={`h-full w-full bg-black bg-opacity-50 absolute right-0 z-[12] top-0 ${menuContext.menuOpen ? 'block xl:hidden' : 'hidden'}`}
		>
			<div
				className="bg-white w-[287px] md:w-[434px] absolute top-0 right-0 h-full"
				ref={ref}
			>
				<div className="flex flex-col gap-y-3 px-[18px] md:px-[34px] bg-cornflower py-4 md:py-[18px]">
					<div className="flex items-center">
						<p className="text-white text-[16px] md:text-[18px] font-semibold break-all">
							{email}
						</p>
						<img
							src={ImagesMobile.iconCloseMenu}
							className="w-[24px] md:w-[28px] h-[24px] md:h-[28px] ml-auto"
							onClick={menuContext.onToggle}
						/>
					</div>
					<div className="flex items-center gap-x-1">
						<IconClock className="h-6 w-6 shrink-0 text-white" />
						<span className="min-w-[46px] md:min-w-[51px] text-base md:text-lg font-semibold text-[#FFE168]">
							{timer}
						</span>
						<span className="text-base md:text-lg font-semibold text-white">
							({extendCount}/8회)
						</span>
						<Button
							type="default"
							disabled={extendLoading}
							className="!h-7 md:!h-[30px] !rounded-[4px] !bg-white px-[14px] !text-base md:!text-lg !font-bold !text-blue2 !border-none ml-1.5"
							onClick={onExtend}
						>
							연장
						</Button>
					</div>
				</div>
				<div className="">
					<ul className="border-b-[8px] border-whiteSmoke px-[18px] md:px-[34px]">
						{menuItems?.map((item) => (
							<li
								key={item?.key}
								className={`flex items-center py-[18px] md:py-[20.5px] border-b-[1px] border-softPeach ${item?.key ? '' : 'hidden'} `}
								onClick={() => onClick(item)}
							>
								<img
									src={item?.icon}
									alt=""
									className="w-[16px] md:w-[20px] h-[16px] md:h-[20px]"
								/>
								<span className="pl-[2.778vw] text-[15px] md:text-[16px] text-font2 font-semibold leading-[17px] md:leading-[19px]">
									{item?.label}
								</span>
								<img
									src={ImagesMobile.iconMoreMenu}
									alt=""
									className="ml-auto w-[16px] md:w-[20px] h-[16px] md:h-[20px]"
								/>
							</li>
						))}
					</ul>
					<ul className="px-[18px] md:px-[34px]">
						<li
							className="flex items-center py-[18px] md:py-[20px] border-b-[1px] border-softPeach"
							onClick={handleClickProfile}
						>
							<img
								src={ImagesMobile?.iconSetting}
								alt=""
								className="w-[16px] md:w-[20px] h-[16px] md:h-[20px]"
							/>
							<span className="pl-[2.778vw] text-[15px] md:text-[16px] text-font2 font-semibold break-all leading-[17px] md:leading-[19px]">
								내 정보 관리
							</span>
							<img
								src={ImagesMobile.iconMoreMenu}
								alt=""
								className="ml-auto w-[16px] md:w-[20px] h-[16px] md:h-[20px]"
							/>
						</li>
						<li
							className="flex items-center py-[18px] md:py-[20.5px] border-b-[1px] border-softPeach"
							onClick={onLogoutClick}
						>
							<img
								src={ImagesMobile?.iconLogout}
								alt=""
								className="w-[16px] md:w-[20px] h-[16px] md:h-[20px]"
							/>
							<span className="pl-[2.778vw] text-[15px] md:text-[16px] text-font2 font-semibold leading-[17px] md:leading-[19px]">
								로그아웃
							</span>
						</li>
					</ul>
				</div>
			</div>
		</div>
	);
};

export default memo(MenuMobile);
